<template>
  <v-card>
    <v-card-title class="text-h5">
      Sähköinen allekirjoitus
    </v-card-title>
    <v-card-text v-if="signers[offererIndex]">
      <p v-if="signers[offererIndex].ssn === ssn && !firstSignature">
        Vahvista tarjouksen allekirjoitus.
      </p>
      <p v-else-if="firstSignature">
        Täydennä tietosi ja vahvista tarjouksen allekirjoitus.
      </p>
      <v-alert
        icon="person"
        prominent
        text
        type="info"
      >
        <b>{{ name }}</b><br>
        {{ ssn }}
      </v-alert>
      <v-form
        v-if="firstSignature"
        ref="signatureForm"
        v-model="signatureFormValid"
      >
        <v-text-field
          v-model="phone"
          outlined
          autocomplete="off"
          label="Puhelin"
          :rules="textFieldRules"
          required
        />
        <v-text-field
          v-model="address"
          outlined
          autocomplete="off"
          label="Osoite"
          class="mb-2"
          :rules="textFieldRules"
          required
        />
        <v-row>
          <v-col
            cols="12"
            md="6"
            class="py-0"
          >
            <v-text-field
              v-model="zip"
              :rules="textFieldRules"
              class="mb-1"
              autocomplete="off"
              required
              outlined
              label="Postinumero"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
            class="py-0"
          >
            <v-text-field
              v-model="city"
              :rules="textFieldRules"
              class="mb-1"
              autocomplete="off"
              outlined
              required
              label="Kaupunki"
            />
          </v-col>
        </v-row>
        <v-text-field
          v-model="nationality"
          outlined
          class="mt-2"
          label="Kansalaisuus"
          :rules="textFieldRules"
          required
        />
        <v-text-field
          v-if="isCompany"
          v-model="companyName"
          :rules="textFieldRules"
          required
          class="mb-1"
          outlined
          label="Yrityksen nimi"
        />
        <v-text-field
          v-if="isCompany"
          v-model="companyNumber"
          :rules="textFieldRules"
          required
          class="mb-1"
          outlined
          label="Y-tunnus"
        />
        <label>Oletko, tai oletko ollut viimeisen vuoden aikana poliittisesti vaikutusvaltainen
          henkilö, tällaisen henkilön perheenjäsen tai läheinen yhtiökumppani?</label>
        <v-switch
          v-model="pep"
          :label="`${pep ? 'Kyllä' : 'Ei'}`"
          class="mt-0"
          color="primary"
        />
        <v-textarea
          v-if="pep"
          v-model="pepDescription"
          :rules="textFieldRules"
          class="mb-1"
          autocomplete="off"
          outlined
          label="PEP-selite"
        />
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer/>
      <v-btn
        color="primary"
        text
        @click="closeDialog"
      >
        Sulje
      </v-btn>
      <v-btn
        :disabled="
          (!signatureFormValid && firstSignature)
        "
        :loading="sending"
        color="primary"
        text
        @click="confirmSignature"
      >
        Vahvista
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import { mapActions } from 'vuex';
import { apiGet, apiPost } from '../api';

export default {
  name: 'NetsSignature',
  props: {
    firstSignature: {
      type: Boolean,
      default: () => false,
    },
    offerers: {
      type: Array,
      default: () => [],
    },
    sellers: {
      type: Array,
      default: () => [],
    },
    responses: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    sending: false,
    signatureFormValid: false,
    name: '',
    ssn: '',
    phone: '',
    address: '',
    zip: '',
    city: '',
    companyName: '',
    companyNumber: '',
    isCompany: false,
    nationality: 'Suomi',
    offererIndex: 0,
    pep: false,
    pepDescription: '',
    textFieldRules: [
      v => !!v || 'Pakollinen kenttä',
    ],
  }),
  computed: {
    signers() {
      if (this.firstSignature === true) return this.offerers;
      if (this.responses.length === 0 || this.responses.length % 2 === 0) {
        return this.sellers;
      }
      return this.offerers;
    },
  },
  mounted() {
    this.getSignatureDetails();
  },
  methods: {
    ...mapActions({
      setCurrentSigner: 'setCurrentSigner',
    }),
    async getSignatureDetails() {
      const propertyId = this.$route.params.id;
      const signatureId = this.$route.query.signature;
      const response = await apiGet(`nets/signature/${propertyId}/${signatureId}`);
      if (response.success) {
        this.name = response.result.name;
        this.ssn = response.result.ssn;
        this.offererIndex = response.result.offerer_index;
        this.phone = this.signers[this.offererIndex].phone;
        this.address = this.signers[this.offererIndex].address;
        this.zip = this.signers[this.offererIndex].zip;
        this.city = this.signers[this.offererIndex].city;
        this.nationality = this.signers[this.offererIndex].nationality;
        this.companyName = this.signers[this.offererIndex].companyName;
        this.companyNumber = this.signers[this.offererIndex].companyNumber;
        this.isCompany = this.signers[this.offererIndex].isCompany;
        this.pep = this.signers[this.offererIndex].pep;
        this.pepDescription = this.signers[this.offererIndex].pepDescription;
      }
    },
    async updateOfferers() {
      if (this.firstSignature && this.signers === this.offerers) {
        const prms = new Promise((resolve) => {
          this.$bus.$emit('loadSignaturesPromise', resolve);
        });
        await prms;
        // await this.$bus.$emit('loadSignatures');
        const params = [...this.offerers];
        params[this.offererIndex] = {
          ...params[this.offererIndex],
          ssn: this.ssn,
          name: this.name,
          address: this.address,
          zip: this.zip,
          city: this.city,
          phone: this.phone,
          nationality: this.nationality,
          companyName: this.companyName,
          companyNumber: this.companyNumber,
          pep: this.pep,
          pepDescription: this.pepDescription,
        };
        await apiPost(`properties/updateofferers/${this.$route.params.offerId}`, params);
      }
    },
    async confirmSignature() {
      this.sending = true;
      const signatureId = this.$route.query.signature;
      await apiGet(`nets/confirm/${signatureId}`);
      if (this.firstSignature) {
        await this.updateOfferers();
      }
      await this.$bus.$emit('loadSignatures');
      await this.$bus.$emit('loadLatestSignatures');
      // await this.$bus.$emit('updateOffer');
      this.$bus.$emit('showThanksForSigning');
      await apiGet('properties/resetsigners');
      this.setCurrentSigner({});
      this.closeDialog();
      this.sending = false;
    },
    closeDialog() {
      this.$bus.$emit('close_dialog');
    },
  },
};
</script>
